import React, { useEffect, useState } from 'react';
import { Form, Input, message, Spin, Card, Alert, Select } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSqlData, updateData } from '../../../services/api';
import { useGetProjSystems } from '../../../hooks/genericHooks';
import type { VwProjSubsystems, ProjSystems } from '../../../types';
import Button from '../../../components/common/Button';

const ToPlnProjSubSystemsEdit: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();

  // Fetch the subsystem by id
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['vw_proj_subsystems', id],
    queryFn: async () => {
      console.info("fetching vw_proj_subsystems",{id});
      if (!id) return null;
      const res = await fetchSqlData('vw_proj_subsystems', 'id', id);
      return res && res.length > 0 ? res[0] : null;
    },
    enabled: !!id,
  });

  // Fetch all available systems for select
  const { data: systemsData, isLoading: systemsLoading } = useGetProjSystems();
  const systemsList: ProjSystems[] = systemsData?.data || [];

  useEffect(() => {
    if (data && data.id) {
      form.setFieldsValue({
        subsystem_id: data.subsystem_id,
        subsystem_name: data.subsystem_name,
        system_id: data.system_id,
        status: data.status,
        imc_group: data.imc_group,
        priority: data.priority,
        requirement: data.requirement,
        remarks: data.remarks,
      });
    }
  }, [data, form]);

  const handleFinish = async (values: any) => {
    if (!id) {
      message.error('Subsystem ID is missing.');
      return;
    }
    setIsUpdating(true);
    try {
      await updateData('to_pln_proj_subsystems', id, {
        subsystem_id: values.subsystem_id,
        subsystem_name: values.subsystem_name,
        system_id: values.system_id,
        status: values.status,
        imc_group: values.imc_group,
        priority: values.priority,
        requirement: values.requirement,
        remarks: values.remarks,
      }, 'id');
      await queryClient.invalidateQueries({ queryKey: ['vw_proj_subsystems'] });
      await queryClient.refetchQueries({ queryKey: ['vw_proj_subsystems'] });
      message.success('Subsystem updated successfully!');
      navigate(-1);
    } catch (err: any) {
      message.error('Update failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading || systemsLoading) return <Spin />;
  if (isError) return <Alert type="error" message="Error loading subsystem" description={error?.message || 'Unknown error'} />;
  if (!data) return <Alert type="warning" message="Subsystem not found" />;

  return (
    <Card variant="outlined" title={`Edit Project Subsystem: ${id}`} style={{ maxWidth: 500, margin: '2rem auto' }}>
      <Form form={form} layout="vertical" onFinish={handleFinish} style={{ gap: 0, rowGap: 0 }}>
        <Form.Item label="Associated System" name="system_id" rules={[{ required: true, message: 'Please select a system' }]} style={{ marginBottom: 8 }}>
          <Select placeholder="Select a system">
            {systemsList.map(sys => (
              <Select.Option key={sys.sys_id} value={sys.sys_id}>{sys.sys_id} - {sys.sys_name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <div style={{ display: 'flex', gap: 8, marginBottom: 0 }}>
          <Form.Item label="Subsystem ID" name="subsystem_id" rules={[{ required: true, message: 'Please enter subsystem ID' }]} style={{ flex: 1, marginBottom: 0 }}>
            <Input />
          </Form.Item>
          <Form.Item label="Status" name="status" rules={[{ required: true, message: 'Please select a status' }]} style={{ flex: 1, marginBottom: 0 }}>
            <Select>
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
              <Select.Option value="out_of_service">Out of Service</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="IMC Group" name="imc_group" style={{ flex: 1, marginBottom: 0 }}>
            <Input type="number" />
          </Form.Item>
        </div>
        <Form.Item label="Subsystem Name" name="subsystem_name" rules={[{ required: true, message: 'Please enter subsystem name' }]} style={{ marginBottom: 8 }}>
          <Input />
        </Form.Item>
        <Form.Item label="Priority" name="priority" style={{ marginBottom: 8 }}>
          <Input type="number" />
        </Form.Item>
        <Form.Item label="Requirement" name="requirement" style={{ marginBottom: 8 }}>
          <Input />
        </Form.Item>
        <Form.Item label="Remarks" name="remarks" style={{ marginBottom: 8 }}>
          <Input />
        </Form.Item>
        <Form.Item style={{ marginBottom: 8 }}>
          <Button action="edit" type="primary" htmlType="submit" loading={isUpdating}>
            Save
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default ToPlnProjSubSystemsEdit;
